import type { TitleType } from "@/config/seoTitles";
import React from "react";
import { Link, useLocation } from "@/lib/router-compat";
import Navbar from "@/components/layout/Navbar";
import PageHierarchySeo from "@/components/seo/PageHierarchySeo";
import { getChildren, normalizePath } from "@/config/urlHierarchy";
import Footer from "@/components/landing/Footer";
import { Card } from "@/components/ui/card";
import SubpageDirectory from "@/components/common/SubpageDirectory";
import { useVisibleChildren } from "@/lib/visibleChildren";
import { useToolsConfig } from "@/hooks/useToolsConfig";
import FaqAccordion from "@/components/common/FaqAccordion";
import NotifyPromoSection from "@/components/notifications/NotifyPromoSection";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export interface ContentSection {
  heading: string;
  body: string | React.ReactNode;
  bullets?: string[];
}

export interface ContentFaqItem {
  q: string;
  a: string;
}

export interface ContentCta {
  label: string;
  to: string;
}

export interface ContentPageProps {
  title: string;
  /** Egen <title>/og:title om den ska skilja sig från H1. */
  seoTitle?: string;
  /** Titelmall, t.ex. "raw" när seoTitle redan är en komplett <title>. */
  seoTitleType?: TitleType;
  /** Egen metabeskrivning om den ska skilja sig från introt. */
  seoDescription?: string;
  /** Blockera indexering av sidan. */
  noindex?: boolean;
  intro: string;
  breadcrumb: { label: string; path?: string }[];
  icon?: React.ReactNode;
  primaryCta?: ContentCta;
  secondaryCta?: ContentCta;
  sections?: ContentSection[];
  highlights?: { title: string; description: string; icon?: React.ReactNode; to?: string }[];
  faq?: ContentFaqItem[];
  /** Tvinga bakgrundston på FAQ-sektionen (för sidor med egna helbredds-sektioner före). */
  faqTone?: "alt" | "surface";
  relatedLinks?: { label: string; to: string }[];
  /** Ersätter standardhjälten, t.ex. med en offertwidget */
  hero?: React.ReactNode;
  /**
   * Bredd på children-sektionen.
   * "full" (default) = ingen wrapper, sidan sköter sin egen bredd (t.ex. /spotpris).
   * "contained" = centrerad max-w-6xl med sidopadding (tjänstesidor med rå text/kort).
   */
  /** Visa automatisk lista över undersidor i kategorin (default: true). */
  showSubpages?: boolean;
  subpagesTitle?: string;
  contentWidth?: "full" | "contained";
  /** Lägg till alternerande vit/ljusgrå bakgrund på varannan sektion. */
  stripeSections?: boolean;
  /** Innehåll som renderas direkt under hero, före highlights/sektioner. */
  topChildren?: React.ReactNode;
  children?: React.ReactNode;
}

const ContentPage: React.FC<ContentPageProps> = ({
  title,
  seoTitle,
  seoTitleType,
  seoDescription,
  noindex = false,
  intro,
  breadcrumb,
  icon,
  primaryCta,
  secondaryCta,
  sections = [],
  highlights: highlightsProp = [],
  faq = [],
  faqTone,
  
  hero,
  showSubpages = true,
  subpagesTitle,
  contentWidth = "full",
  topChildren,
  children,
  stripeSections = true,
}) => {
  const { pathname } = useLocation();
  const path = normalizePath(pathname);

  const { isPathEnabled } = useToolsConfig();
  // Länka aldrig till sidor som stängts av i adminpanelen.
  const highlights = highlightsProp.filter((h) => !h.to || isPathEnabled(h.to));

  const visibleChildren = useVisibleChildren(path);
  const hasSubpages = showSubpages && visibleChildren.length > 0;

  /**
   * Sektionslogik: varje sektion som faktiskt renderas läggs i en lista och
   * får sin bakgrundston tilldelad i JS. Det gör att alterneringen alltid
   * blir korrekt även när sektioner renderas villkorligt (till skillnad mot
   * nth-child i CSS, som räknar även icke-sektionsnoder och tappar takten
   * när t.ex. FAQ eller undersidor saknas).
   */
  type Tone = "alt" | "surface";
  const flip = (t: Tone): Tone => (t === "surface" ? "alt" : "surface");

  const heroNode = hero === undefined ? (
    <section
      key="hero"
      className="relative overflow-hidden border-b border-border bg-gradient-to-b from-muted/60 via-background to-background py-12 md:py-20"
    >
      <div className="page-container text-center">
        {icon && <div className="text-primary inline-flex mb-4">{icon}</div>}
        <h1 className="heading-1 mb-3 md:mb-4">{title}</h1>
        <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">{intro}</p>
        {(primaryCta || secondaryCta) && (
          <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3 mt-6 md:mt-8">
            {primaryCta && (
              <Button asChild size="lg" variant="cta" className="w-full sm:w-auto">
                <Link to={primaryCta.to}>
                  {primaryCta.label} <ArrowRight className="ml-2" size={18} />
                </Link>
              </Button>
            )}
            {secondaryCta && (
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
                <Link to={secondaryCta.to}>{secondaryCta.label}</Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  ) : hero;

  // Sektioner som ska ingå i alterneringen, i renderingsordning.
  const striped: { id: string; render: (tone: Tone | undefined) => React.ReactNode }[] = [];

  if (topChildren) {
    striped.push({
      id: "top",
      render: (tone) => (
        <section key="top" className="py-12 md:py-16" data-stripe={tone}>
          <div className="page-container">{topChildren}</div>
        </section>
      ),
    });
  }

  if (highlights.length > 0) {
    striped.push({
      id: "highlights",
      render: (tone) => (
        <section key="highlights" className="py-12 md:py-16" data-stripe={tone}>
          <div className="page-container grid grid-cols-1 md:grid-cols-3 gap-6">
            {highlights.map((h, i) => {
              const card = (
                <Card className="rounded-2xl border-border p-6 shadow-xs transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md h-full">
                  {h.icon && <div className="text-primary mb-3">{h.icon}</div>}
                  <h3 className="heading-3 mb-2">{h.title}</h3>
                  <p className="text-muted-foreground text-sm">{h.description}</p>
                </Card>
              );
              return h.to ? (
                <Link
                  key={i}
                  to={h.to}
                  className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-2xl"
                >
                  {card}
                </Link>
              ) : (
                <div key={i}>{card}</div>
              );
            })}
          </div>
        </section>
      ),
    });
  }

  if (sections.length > 0) {
    striped.push({
      id: "sections",
      render: (tone) => (
        <section key="sections" className="py-12 md:py-16" data-stripe={tone}>
          <div className="page-container space-y-8 md:space-y-10">
            {sections.map((s, i) => (
              <article key={i}>
                <h2 className="heading-2 mb-3 md:mb-4">{s.heading}</h2>
                {typeof s.body === "string" ? (
                  <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                ) : (
                  <div className="text-muted-foreground leading-relaxed">{s.body}</div>
                )}
                {s.bullets && (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-foreground">
                        <CheckCircle2 className="text-primary shrink-0 mt-1" size={18} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </section>
      ),
    });
  }

  if (children) {
    striped.push({
      id: "children",
      // Egen bredd/bakgrund hanteras av sidan själv när contentWidth = "full".
      render: (tone) => (
        <section
          key="children"
          className={hero === null && contentWidth === "full" ? "" : "py-12 md:py-16"}
          data-stripe={tone}
        >
          {contentWidth === "contained" ? <div className="page-container">{children}</div> : children}
        </section>
      ),
    });
  }

  if (faq.length > 0) {
    striped.push({
      id: "faq",
      render: (tone) => (
        <section key="faq" className="py-12 md:py-16" data-stripe={tone}>
          <div className="page-container">
            <h2 className="heading-2 mb-6 text-center">Vanliga frågor</h2>
            <FaqAccordion items={faq.map((f) => ({ question: f.q, answer: f.a }))} />
          </div>
        </section>
      ),
    });
  }

  // Notiskampanj på alla elpris- och spotprissidor
  if (/^\/(elpriser|spotpris)(\/|$)/.test(path)) {
    striped.push({
      id: "notify-promo",
      render: (tone) => <NotifyPromoSection key="notify-promo" tone={tone} />,
    });
  }

  if (hasSubpages) {
    striped.push({
      id: "subpages",
      render: (tone) => (
        <SubpageDirectory key="subpages" path={path} title={subpagesTitle} tone={tone} />
      ),
    });
  }

  // "Relaterat"-länksektionen är borttagen – den låg efter tabellerna och
  // uppfattades som brus. relatedLinks-propen finns kvar för bakåtkompatibilitet
  // men renderas inte längre.

  // Starttonen väljs så att en ev. tvingad FAQ-ton hamnar rätt i kedjan.
  let startTone: Tone = "surface";
  if (faqTone) {
    const faqIndex = striped.findIndex((s) => s.id === "faq");
    if (faqIndex >= 0) startTone = faqIndex % 2 === 0 ? faqTone : flip(faqTone);
  }

  const rendered = striped.map((s, i) =>
    s.render(stripeSections ? (i % 2 === 0 ? startTone : flip(startTone)) : undefined),
  );

  return (
    <div className="min-h-dvh bg-background">
      <Navbar />
      <PageHierarchySeo
        path={path}
        title={seoTitle ?? title}
        titleType={seoTitleType ?? "page"}
        description={seoDescription ?? intro}
        noindex={noindex}
      />

      <main>
        {heroNode}
        {/* Sidor utan hero behöver ändå en H1 för SEO och skärmläsare */}
        {hero === null && <h1 className="sr-only">{title}</h1>}
        {rendered}
      </main>

      <Footer />
    </div>
  );
};

export default ContentPage;
